import { TBookingContext } from '@providers/booking/types'
import { i18WithParams as t } from '@shared/locale'
import { DEFAULT_TIMEZONE, getAvailableProviderSlots, todayInTZ } from 'components-ui/src/shared/dates'
import startOfDay from 'date-fns/startOfDay'
import sub from 'date-fns/sub'
import cloneDeep from 'lodash.clonedeep'
import { LocationBookingConfClientDto, ProviderClientDto } from 'service-api/src/types/api'

export const formatStayTime = (minutes: number | null) => {
  if (minutes === null) {
    return ''
  }

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  const formatMinutes = (value: number) =>
    t(value === 1 ? 'global.time.minute' : 'global.time.minutes', { count: String(value) })

  if (hours === 0) {
    return formatMinutes(remainingMinutes)
  }

  const formattedHours = t(hours === 1 ? 'global.time.hour' : 'global.time.hours', { count: String(hours) })
  if (remainingMinutes === 0) {
    return formattedHours
  }

  return t('global.time.hoursAndMinutes', {
    hours: formattedHours,
    minutes: formatMinutes(remainingMinutes),
  })
}

export const firstSelectedDate = (bookingCtx: TBookingContext) => {
  if (bookingCtx.serviceTypeName === 'daycare') {
    if (!bookingCtx.cart.groups[0].daycareSelected) {
      return new Date()
    }
    const _cloned = cloneDeep(bookingCtx.cart.groups[0].daycareSelected!)
    return _cloned.sort(({ id: idA }, { id: idB }) => idA - idB)?.[0].date
  } else {
    return bookingCtx.startDate!
  }
}

export const getOfferServiceInterval = (stConfig?: LocationBookingConfClientDto, defaultInterval = 30) => {
  return stConfig?.arrivalTimeInterval || defaultInterval
}

export const isDataInCache = (startDate: Date, endDate: Date, state: TBookingContext) => {
  const firstDate = startOfDay(startDate).getTime()
  const lastDate = startOfDay(sub(endDate, { days: 1 })).getTime()
  const cache = state.data.temperTestAvailability

  return Boolean(cache?.some(({ id }) => id === firstDate) && cache.some(({ id }) => id === lastDate))
}

const getDayAvailabilities = (day: Date, state: TBookingContext) =>
  state.data.temperTestAvailability
    ?.find(({ id }) => id === startOfDay(day).getTime())
    ?.availabilityGroups.map(({ availabilities }) => availabilities?.[0])

export const getProviderSlots = (day: Date, state: TBookingContext, provider: ProviderClientDto) => {
  if (!provider.id) {
    return []
  }

  const availabilities = getDayAvailabilities(day, state)
  if (!availabilities?.length) {
    return []
  }

  const providers = availabilities.map((availability) =>
    availability?.providers?.find(({ available, id }) => available && id === provider.id)
  )
  if (providers.some((currentProvider) => !currentProvider)) {
    return []
  }

  const currentDate = todayInTZ(state.config.location?.timezone || DEFAULT_TIMEZONE)
  const slotsByAvailability = providers.map(
    (currentProvider) => getAvailableProviderSlots(currentProvider, day, currentDate) || []
  )
  const [firstSlots, ...remainingSlots] = slotsByAvailability

  return firstSlots.filter(({ slotStartTime }) =>
    remainingSlots.every((slots) => slots.some((slot) => slot.slotStartTime === slotStartTime))
  )
}

export const isDayAvailable = (
  day: Date,
  state: TBookingContext,
  provider: ProviderClientDto | null,
  willUseSlots: boolean
) => {
  const availabilities = getDayAvailabilities(day, state)
  if (!availabilities?.length || availabilities.some((availability) => !availability?.available)) {
    return false
  }

  if (!willUseSlots) {
    return true
  }

  if (!provider) {
    return false
  }

  const isProviderAvailable = availabilities.every((availability) =>
    availability?.providers?.some(({ available, id }) => available && id === provider.id)
  )

  return isProviderAvailable && getProviderSlots(day, state, provider).length > 0
}
